import React, { FC, useState, useRef, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { NfcTagReader } from './NfcTagReader';
import Button from '../Button';
import { BookingResource, BookingSlot } from '../../types/booking';
import { IconCheck, IconLogOut, IconArrowRight } from '../icons';
import { DateTime } from 'luxon';
import { Modal } from '../Modal';
import { cn } from '../../utils/classNames';
import { useBookingMutations } from '../../hooks/useBookings';
import { useApiError } from '../../hooks/useApiError';
import { ResourceIcon } from './ResourceIcon';
import { useAuth } from '../../hooks/useAuth';
import { SLOT_STYLES } from './slotStyles';
import { useIdentity } from '../../providers/IdentityProvider';

const enum ModalState {
  Initial = 'initial',
  Confirming = 'confirming',
  Processing = 'processing',
  Success = 'success',
  Error = 'error',
}

interface BookingModalProps {
  mode: 'book' | 'cancel';
  resource: BookingResource;
  timeSlot: BookingSlot;
  onClose: () => void;
  isAuthenticated?: boolean;
}

interface SuccessViewProps {
  mode: BookingModalProps['mode'];
  resource: BookingResource;
  startTime: DateTime;
  endTime: DateTime;
  pin?: string;
  isAuthenticated?: boolean;
  onClose: () => void;
  onLogout: () => void;
}

const SuccessView: FC<SuccessViewProps> = ({
  mode,
  resource,
  startTime,
  endTime,
  pin,
  isAuthenticated,
  onClose,
  onLogout,
}) => (
  <div className="flex flex-col items-center gap-6 py-4">
    <div className="size-16 rounded-full bg-green-100 flex items-center justify-center">
      <IconCheck className="size-8 text-green-600" />
    </div>

    <div className="text-center">
      <h2 className="text-xl font-medium mb-2">
        {mode === 'book' ? (
          <FormattedMessage defaultMessage="Bokning bekräftad!" />
        ) : (
          <FormattedMessage defaultMessage="Avbokning bekräftad!" />
        )}
      </h2>
      <p className="text-gray-600">
        {resource.name}
        <br />
        {startTime.toFormat('cccc d MMMM')}
        <br />
        {startTime.toFormat('HH:mm')} - {endTime.toFormat('HH:mm')}
      </p>
      {mode === 'book' && pin && (
        <div className="mt-4 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">
            <FormattedMessage defaultMessage="Din PIN-kod är" />
          </p>
          <p className="text-3xl font-bold tracking-wider text-gray-900 text-center">
            {pin}
          </p>
        </div>
      )}
    </div>

    <div className="flex flex-col w-full gap-3">
      {isAuthenticated && (
        <Button
          onClick={onLogout}
          className="flex items-center justify-center gap-2 !bg-gray-200 !text-gray-900 hover:!bg-gray-200"
        >
          <IconLogOut className="size-5" />
          <FormattedMessage defaultMessage="Logga ut" />
        </Button>
      )}
      <Button
        onClick={onClose}
        className="flex items-center justify-center gap-2 !bg-gray-900"
      >
        <FormattedMessage defaultMessage="Stäng och fortsätt" />
        <IconArrowRight className="size-5" />
      </Button>
    </div>
  </div>
);

export const BookingModal: FC<BookingModalProps> = ({
  mode,
  resource,
  timeSlot,
  onClose,
}) => {
  const [state, setState] = useState<ModalState>(ModalState.Initial);
  const [error, setError] = useState<unknown>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const { logout, isAuthenticated, nfcKeyId, pin, login } = useAuth();
  const { getErrorMessage } = useApiError();
  const { create: createBooking, cancel: cancelBooking } =
    useBookingMutations();
  const { id: deviceId } = useIdentity(
    useCallback((state: { id?: string }) => ({ id: state.id }), []),
  );
  const isDev = process.env.NODE_ENV === 'development';

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    onClose();
  }, [onClose]);

  const handleLogout = useCallback(() => {
    logout();
    handleClose();
  }, [handleClose, logout]);

  const handleNfcAuthenticated = useCallback(
    (key: string) => {
      if (!key || (nfcKeyId && key !== nfcKeyId) || !deviceId) {
        handleLogout();
        return;
      }

      setState(ModalState.Processing);
      setError(null);

      if (mode === 'book') {
        console.log(
          `[BookingModal] Creating booking with deviceId: ${deviceId}, resourceId: ${resource.id}, keyNfcId: ${key}`,
        );
        const fixedStartTime = DateTime.fromISO(timeSlot.start).setZone(
          'local',
        );
        const fixedEndTime = DateTime.fromISO(timeSlot.end).setZone('local');
        createBooking
          .mutateAsync({
            deviceId: deviceId,
            resourceId: resource.id,
            startTime: fixedStartTime.toISO() || '',
            endTime: fixedEndTime.toISO() || '',
            keyNfcId: key,
            subject: `Bokning av ${resource.name}`,
          })
          .then((result) => {
            login(key, result.pin || '');

            setState(ModalState.Success);
            closeTimeoutRef.current = setTimeout(onClose, 20000);
          })
          .catch((err) => {
            console.error('[BookingModal] Error creating booking:', err);
            if (key !== nfcKeyId) {
              handleLogout();
            }
            setState(ModalState.Error);
            setError(err);

            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            throw err;
          });
      } else {
        const bookingId = timeSlot.bookingId || timeSlot.id;
        if (!bookingId) {
          setError('No booking ID found for cancellation');
          return;
        }
        console.log(
          `[BookingModal] Cancelling booking ${bookingId} with deviceId: ${deviceId}, keyNfcId: ${key}`,
        );
        cancelBooking
          .mutateAsync({
            deviceId: deviceId,
            bookingId,
            resourceId: resource.id,
            keyNfcId: key,
          })
          .then(() => {
            setState(ModalState.Success);
            closeTimeoutRef.current = setTimeout(onClose, 20000);
          })
          .catch((err) => {
            console.error('[BookingModal] Error cancelling booking:', err);
            setState(ModalState.Error);
            setError(err);

            if (closeTimeoutRef.current) {
              clearTimeout(closeTimeoutRef.current);
            }
            throw err;
          });
      }
    },
    [
      nfcKeyId,
      deviceId,
      mode,
      handleLogout,
      resource.id,
      resource.name,
      timeSlot.start,
      timeSlot.end,
      timeSlot.bookingId,
      timeSlot.id,
      createBooking,
      login,
      cancelBooking,
      onClose,
    ],
  );

  const startTime = DateTime.fromISO(timeSlot.start).setZone('local');
  const endTime = DateTime.fromISO(timeSlot.end).setZone('local');

  return (
    <Modal
      open={true}
      onClose={onClose}
      className="!p-0 !bg-transparent backdrop-blur-none"
    >
      <div className="bg-white p-8 w-[480px] rounded-lg flex flex-col">
        {state === ModalState.Success ? (
          <SuccessView
            mode={mode}
            resource={resource}
            startTime={startTime}
            endTime={endTime}
            pin={pin}
            isAuthenticated={isAuthenticated}
            onClose={handleClose}
            onLogout={handleLogout}
          />
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-center text-center justify-between">
              <h2 className="text-2xl font-medium flex items-center gap-3">
                {mode === 'book' ? (
                  <FormattedMessage defaultMessage="Bekräfta bokning" />
                ) : (
                  <FormattedMessage defaultMessage="Bekräfta avbokning" />
                )}
              </h2>
            </div>

            {state === ModalState.Error && (
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <h3 className="font-medium text-red-800">
                    {mode === 'book' ? (
                      <FormattedMessage defaultMessage="Kunde inte boka tid" />
                    ) : (
                      <FormattedMessage defaultMessage="Kunde inte avboka tid" />
                    )}
                  </h3>
                  <p className="text-sm text-red-600 mt-1">
                    {getErrorMessage(
                      error,
                      mode === 'book'
                        ? 'POST /v2/bookings'
                        : 'DELETE /v2/bookings/:id',
                      { userFriendly: true },
                    )}
                  </p>
                </div>
              </div>
            )}

            <div
              className="flex gap-3 rounded-lg p-3"
              style={{
                background: SLOT_STYLES[timeSlot.status].color,
                color: SLOT_STYLES[timeSlot.status].textColor,
              }}
            >
              <ResourceIcon type={resource.type} className="size-14" />
              <div className="flex flex-col justify-center">
                <b>{resource.name}</b>
                <p>{startTime.toFormat('d MMMM')}</p>
                <p>
                  {startTime.toFormat('HH:mm')} - {endTime.toFormat('HH:mm')}
                </p>
              </div>
            </div>

            <p className="text-center text-gray-900 text-lg font-medium">
              {mode === 'book' ? (
                <FormattedMessage defaultMessage="Håll fram din digitala nyckel för att boka" />
              ) : (
                <FormattedMessage defaultMessage="Håll fram din digitala nyckel för att avboka" />
              )}
            </p>

            <NfcTagReader
              className={cn(
                'w-64 h-64 mx-auto',
                state === ModalState.Processing &&
                  'opacity-50 pointer-events-none',
              )}
              onAuthenticated={handleNfcAuthenticated}
            />

            {state === ModalState.Processing ? (
              <div className="text-center text-gray-600">
                <FormattedMessage defaultMessage="Bearbetar..." />
              </div>
            ) : (
              <Button onClick={onClose}>
                <FormattedMessage defaultMessage="Avbryt" />
              </Button>
            )}

            {state === ModalState.Error && isDev && error instanceof Error && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-xs text-gray-500 font-mono">
                  {error.message}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};
